import { ActionCreatorWithPayload } from '@reduxjs/toolkit'
import { produce } from 'immer'
import { Country, MetricGroup, MetricValue, SimplifyMetricGroup } from '../types'
import _ from 'lodash'
import { METRIC_ENDPOINT_RANK, PILLAR_INFO } from '../constant'
import { filterRankingMetrics } from './vibrancyUtils'

export const selectCountryData = (data: Country[], currentlySelectedCountries?: string[]) => {
  // If currentlySelectedCountries is empty
  if (
    !currentlySelectedCountries?.length ||
    currentlySelectedCountries?.length === 0 ||
    currentlySelectedCountries === undefined
  ) {
    throw new Error('Currently selected countries is empty')
  }

  // No data provided
  if (data.length === 0 || !data.length) {
    throw new Error('No country data provided')
  }

  // If currentlySelectedCountries only contains country code
  currentlySelectedCountries.forEach((code) => {
    const country = data.find((c) => c.countryLongCode === code)
    if (!country) {
      throw new Error('Currently selected countries contains invalid country code')
    }
  })

  // Filtering according to the country code
  const initialCountries = data.filter(
    (element) => currentlySelectedCountries?.includes(element.metadatas[0]?.Code?.toString()),
  )

  return currentlySelectedCountries.map((countryCode): Country => {
    return initialCountries.find((country) => country.countryLongCode === countryCode)!
  })
}

export const simplifyMetricGroup = (group: MetricGroup, metricValueFilter: MetricValue): SimplifyMetricGroup => {
  const simplifiedMetricGroup: SimplifyMetricGroup = []

  group?.metrics?.forEach((values) => {
    if (metricValueFilter === 'perCapita' && values.metricName.includes('PC')) {
      let metricName = values.metricName.slice(0, values.metricName.length - 3)

      simplifiedMetricGroup.push({
        metricName,
        metricShortName: values.shortname_raw,
        metricShortNameScaled: values.shortname_scaled,
        multiplier: values.multiplier,
      })
    } else if (metricValueFilter === 'absolute' && values.metricName.includes('PC') === false) {
      simplifiedMetricGroup.push({
        metricName: values.metricName,
        metricShortName: values.shortname_raw,
        metricShortNameScaled: values.shortname_scaled,
        multiplier: values.multiplier,
      })
    }
  })

  return simplifiedMetricGroup
}

export const getDataAgainstMetricKey = (
  countryCode: Country['countryCode'],
  data: Country[],
  configuration: {
    shortnameScaled: string
    shortNameRaw: string
    currentSelectedYear: number
    metricType: MetricValue
  },
) => {
  const { shortnameScaled, shortNameRaw, currentSelectedYear } = configuration
  let returnData: { value: number; score: number; ranking: number; countCountry: number } = {
    value: 0,
    score: 0,
    ranking: 0,
    countCountry: 0,
  }

  const metadataOfCountry = data
    .find((country) => country.countryCode === countryCode)
    ?.metadatas.find((metadata) => metadata.PublishYear === currentSelectedYear)!


  if (!metadataOfCountry) return returnData
  const computeRanking = getRankByMetric4Country(data, countryCode, currentSelectedYear, shortnameScaled)

  returnData.value = metadataOfCountry[shortNameRaw] !== '-' ? ((metadataOfCountry[shortNameRaw]) as number) : 0
  returnData.score =
    metadataOfCountry[`${shortnameScaled}`] !== '-' ? ((metadataOfCountry[`${shortnameScaled}`]) as number) : 0
  returnData.ranking =
    metadataOfCountry[`${shortnameScaled}${METRIC_ENDPOINT_RANK}`] !== '-'
      ? computeRanking?.rank || 0
      : // ? (metadataOfCountry[`${shortnameScaled}${METRIC_ENDPOINT_RANK}`] as number)
      0
  returnData.countCountry = computeRanking.countryNum
  return returnData
}

export const getRankByMetric4Country = (
  data: Country[],
  countryCode: Country['countryCode'],
  publishYear: number,
  shortnameScaled: string,
) => {
  const yearData = data.filter((country) =>
    country.metadatas.some(
      (metadata) =>
        metadata.PublishYear === publishYear &&
        metadata[shortnameScaled] !== '-' &&
        !isNaN(Number(metadata[shortnameScaled])),
    ),
  )
  const metricValues = yearData.map((country) => ({
    countryName: country.countryName,
    countryCode: country.countryCode,
    value: country.metadatas.find((metadata) => metadata.PublishYear === publishYear)?.[shortnameScaled] as number,
  }))

  metricValues.sort((a, b) => {
    if (a.value !== b.value) {
      return b.value - a.value;
    }
    return a.countryName.localeCompare(b.countryName);
  })

  const rank = metricValues.findIndex((metric) => metric.countryCode === countryCode) + 1

  return { rank, countryNum: metricValues?.length }
}

export const calculateCountryMetricScore = (countryCode: Country['countryCode'], data: Country[]): any => {
  return PILLAR_INFO.reduce((prev, cur) => {
    const countryData = data.find((ele) => ele.countryCode === countryCode)
    const pillarScore = countryData && (countryData?.[`${cur.dataKey}Share`] as number)
    if (!pillarScore) return prev
    return {
      ...prev,
      [cur.dataKey]: pillarScore,
    }
  }, {})
}

export const calculateCountryMetricWeight = (countryCode: Country['countryCode'], data: Country[]): any => {
  return PILLAR_INFO.reduce((prev, cur) => {
    const countryData = data.find((ele) => ele.countryCode === countryCode)
    const pillarScore = countryData && (countryData?.[`${cur.dataKey}Weight`] as number)
    if (!pillarScore) return prev
    return {
      ...prev,
      [cur.dataKey]: pillarScore,
    }
  }, {})
}

export const getCountryVibrancyRank = (countryCode: Country['countryCode'], countries: Country[]) => {
  return countries.find((country) => country.countryCode === countryCode)!.ranking
}

export const selectDefaultSelectedCountries = (
  countryData: Country[],
  setComparingCountries: ActionCreatorWithPayload<
    {
      countries: string[]
    },
    string
  >,
) => {
  if (!countryData?.length) {
    throw new Error('Invalid argument provided')
  }

  const topCountries: Array<string> = countryData.slice(0, 4).map((country) => country.countryLongCode)

  if (topCountries.length === 4) {
    setComparingCountries({ countries: topCountries })
  }
}

export const calculateMetricGroupForTable = (
  metricGroupsParam: MetricGroup[],
  { metricValue }: { metricValue: MetricValue },
): MetricGroup[] => {
  const filteredMetricGroups = filterRankingMetrics(metricGroupsParam)
  const nextState = produce({ metricGroups: filteredMetricGroups }, ({ metricGroups }) => {
    const totalPillarPercentage = _.sum(metricGroups.map((metricGroup) => metricGroup.multiplier))
    if (totalPillarPercentage !== 1) {
      const scaledValue = metricGroups.reduce((prev, cur) => cur.multiplier + prev, 0)
      metricGroups.forEach((metricGroup) => (metricGroup.multiplier = metricGroup.multiplier / scaledValue))
    }

    metricGroups.forEach((metricGroup) => {
      const totalMetricPercentage = _.sum(
        metricGroup.metrics
          .map((metric) => (metric.metricValue === metricValue ? metric.multiplier : null))
          .filter((metric) => metric !== null),
      )

      if (totalMetricPercentage !== 1) {
        const scaledValue = metricGroup.metrics.reduce(
          (prev, cur) => (cur.metricValue === metricValue ? cur.multiplier + prev : prev),
          0,
        )

        metricGroup.metrics.forEach(
          (metric) => metric.metricValue === metricValue && (metric.multiplier = metric.multiplier / scaledValue),
        )
      }
    })
  })

  return nextState.metricGroups
}

// New function for metric weight adjustment for report tables when no value is present for that pillar
// export const calculateMetricGroupForReportTable = (
//   metricGroupsParam: MetricGroup[],
//   { metricValue, countryShortCode, countryData, currentYear }: { metricValue: MetricValue, countryShortCode: string, countryData: Country[], currentYear: number },
// ): MetricGroup[] => {
//   const flatMetricData = metricGroupsParam.flatMap((group) => {
//     const simplifiedMetricGroup = simplifyMetricGroup(group, metricValue)
//     return simplifiedMetricGroup.map(eachGroup => {
//       return {
//         ...eachGroup, data: getDataAgainstMetricKey(countryShortCode, countryData, {
//           shortnameScaled: `${eachGroup.metricShortNameScaled}_display` as string,
//           shortNameRaw: eachGroup.metricShortName as string,
//           currentSelectedYear: currentYear,
//           metricType: metricValue,
//         })
//       }
//     })
//   })
//   const nextState = produce({ metricGroups: metricGroupsParam }, ({ metricGroups }) => {
//     const totalPillarPercentage = _.sum(metricGroups.map((metricGroup) => metricGroup.multiplier))
//     if (totalPillarPercentage !== 1) {
//       const scaledValue = metricGroups.reduce((prev, cur) => cur.multiplier + prev, 0)
//       metricGroups.forEach((metricGroup) => {
//         metricGroup.multiplier = metricGroup.multiplier / scaledValue
//       })
//     }

//     metricGroups.forEach((metricGroup) => {
//       const totalMetricPercentage = _.sum(
//         metricGroup.metrics
//           .map((metric) => {
//             if (metric.metricValue === metricValue) {
//               const metricData = flatMetricData.find(i => i.metricShortNameScaled === metric.shortname_scaled)
//               if (isNaN(Number(metricData?.data.value))) {
//                 return 0
//               } else {
//                 return metric.multiplier
//               }
//             } else {
//               return null
//             }
//           })
//           .filter((metric) => metric !== null),
//       )
//       if (totalMetricPercentage !== 1) {
//         const scaledValue = metricGroup.metrics.reduce(
//           (prev, cur) => {
//             if (cur.metricValue === metricValue) {
//               const metricData = flatMetricData.find(i => i.metricShortNameScaled === cur.shortname_scaled)
//               if (isNaN(Number(metricData?.data.value))) {
//                 return prev + 0
//               } else {
//                 return cur.multiplier + prev
//               }
//             } else {
//               return prev + 0
//             }
//           },
//           0,
//         )
//         metricGroup.metrics.forEach(
//           (metric) => metric.metricValue === metricValue && (metric.multiplier = metric.multiplier / scaledValue),
//         )
//       }
//       const metricScoreSum = metricGroup.metrics.reduce((prev, cur) => {
//         if (cur.metricValue === metricValue) {
//           const metricData = flatMetricData.find(i => i.metricShortNameScaled === cur.shortname_scaled)
//           if (isNaN(Number(metricData?.data.value))) {
//             return prev + 0
//           } else {
//             return prev + cur.multiplier * (metricData?.data && !isNaN(metricData.data.score) ? metricData?.data.score : 0)
//           }
//         } else {
//           return prev + 0
//         }
//       }, 0);
//       metricGroup.adjustedPillarScore = metricScoreSum * metricGroup.multiplier
//     })
//   })

//   return nextState.metricGroups
// }
